// Shared styles for consistent design across components
export const sharedStyles = {
  // Container Styles
  containerClasses: "relative overflow-hidden",

  containerStyles: {
    background: "var(--glass-bg-primary)",
    border: "1px solid var(--glass-border)",
    borderRadius: "20px",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    boxShadow: "var(--glass-shadow)",
  },

  // Glass Card Variants
  glassCard: {
    background: "var(--glass-bg-primary)",
    border: "1px solid var(--glass-border)",
    borderRadius: "16px",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    boxShadow: "var(--glass-shadow)",
  },

  glassCardSubtle: {
    background: "var(--glass-bg-subtle)",
    border: "1px solid var(--glass-border)",
    borderRadius: "12px",
    backdropFilter: "blur(15px) saturate(150%)",
    WebkitBackdropFilter: "blur(15px) saturate(150%)",
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.04)",
  },

  // Stroke effect for top of cards
  strokeStyles: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%)',
    pointerEvents: 'none',
  },

  // Button Styles
  primaryButton: {
    background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25), 0px 1px 2px rgba(26, 31, 46, 0.08)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    position: 'relative',
    overflow: 'hidden'
  },

  secondaryButton: {
    background: 'var(--bg-surface)',
    color: 'var(--accent-brown)',
    border: '2px solid var(--border-subtle)',
    borderRadius: '10px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0px 2px 4px rgba(26, 31, 46, 0.06)',
  },

  outlineButton: {
    background: 'transparent',
    color: 'var(--accent-primary)',
    border: '2px solid var(--accent-primary)',
    borderRadius: '10px',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Input Styles
  inputStyles: {
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    border: '2px solid var(--border-subtle)',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    width: '100%',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    boxShadow: 'inset 0px 1px 2px rgba(26, 31, 46, 0.06)',
    outline: 'none',
    '&:hover': {
      borderColor: 'var(--accent-light-brown)'
    },
    '&:focus': {
      borderColor: 'var(--accent-primary)',
      boxShadow: '0 0 0 3px rgba(234, 88, 12, 0.1), inset 0px 1px 2px rgba(26, 31, 46, 0.06)'
    }
  },

  // Header Styles
  headerStyles: {
    background: "var(--glass-bg-primary)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderBottom: "1px solid var(--glass-border)",
    boxShadow: "var(--glass-shadow)",
  },

  // Page Layout
  pageContainer: {
    minHeight: '100vh',
    background: 'var(--bg-primary)',
    display: 'flex',
    flexDirection: 'column'
  },

  // Typography
  heading: {
    fontSize: '2rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: 0
  },

  subheading: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0
  },

  bodyText: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: '1.6',
    margin: 0
  },

  captionText: {
    fontSize: '0.875rem',
    color: 'var(--text-muted)',
    margin: 0
  },

  // Color Palette
  colors: {
    // Base colors
    background: 'var(--bg-primary)',
    surface: 'var(--bg-surface)',
    surfaceAlt: 'var(--bg-surface-alt)',

    // Primary - Orange
    primary: 'var(--accent-primary)',
    primaryDark: 'var(--accent-secondary)',
    primaryLight: 'var(--accent-primary-light)',

    // Secondary - Deep teal (complementary contrast)
    secondary: '#1E6B7F',       // Keep hex or define var
    secondaryLight: '#E6F4F7',  // Keep hex

    // Accent - Warm gold for premium features
    accent: '#D4A574',          // Keep hex
    accentLight: '#FBF6EE',     // Keep hex

    // Text hierarchy
    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-secondary)',
    textTertiary: 'var(--text-tertiary)',
    textMuted: 'var(--text-muted)',

    // Semantic colors
    success: '#0F9D58',         // Google green
    successLight: '#E6F4EA',
    successDark: '#0B8043',
    warning: '#F4B400',         // Warm amber
    error: '#DB4437',           // Google red
    errorLight: '#FCE8E6',
    errorDark: '#C5221F',
    info: '#1E6B7F',           // Teal

    // Glass effects
    glassBackground: 'var(--glass-bg-primary)',
    glassBackgroundSubtle: 'var(--glass-bg-subtle)',
    glassBorder: 'var(--glass-border)',
  },

  // Animations and Transitions
  transitions: {
    default: 'all 0.2s ease',
    smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 0.15s ease-in-out',
  }
};

// Helper functions for common style combinations
export const createGlassCard = (variant = 'default', customStyles = {}) => {
  const baseCard = variant === 'subtle' ? sharedStyles.glassCardSubtle : sharedStyles.glassCard;
  return {
    ...baseCard,
    ...customStyles
  };
};

export const createButton = (variant = 'primary', customStyles = {}) => {
  let baseButton;
  switch (variant) {
    case 'secondary':
      baseButton = sharedStyles.secondaryButton;
      break;
    case 'outline':
      baseButton = sharedStyles.outlineButton;
      break;
    default:
      baseButton = sharedStyles.primaryButton;
  }

  return {
    ...baseButton,
    ...customStyles
  };
};

export default sharedStyles;